import React, { useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useTranslation } from 'react-i18next'
import { Skeleton, PreviewImage, Dropdown, Loader } from 'components'
import { IconHistory, IconCheckCircle } from 'components/Icons'
import {
  getImageFileName,
  getValueByLanguage,
  findSelected,
} from 'helpers/common'
import { getLabel } from 'helpers/language'
import { WARRANTY_TYPE } from 'types/enums'
import {
  TicketLabel,
  useFindAllTaskIssue,
  useFindAllTaskType,
  filterOptionBuilder,
  updateTask,
  IUpdateTaskType,
} from 'features/Ticket'

import ProductWarranty from './TicketProductWarranty'

import type { ITicketProductInfo } from 'features/Ticket/TicketProductInfo'
import type { TicketLabelProps } from 'features/Ticket/TicketLabel'

const base = 'f-ticket-product-info'
const ISSUE_TYPE_TITLE = 'ticket:issueType.title'
const TASK_TYPE_TITLE = 'ticket:type.title'
export const renderThumbnail = (
  isLoading: boolean,
  photos: string[],
): JSX.Element => {
  const list = photos?.map((item, index) => {
    return {
      src: getImageFileName(photos[index]),
    }
  })

  return isLoading ? (
    <Skeleton.whiteSkeleton borderRadius='8px' height='160px' width='160px' />
  ) : (
    <PreviewImage className={`${base}__thumbnail`} list={list || []} />
  )
}

const setValue = (value?: string | null): string => (value ? `${value}` : '-')
export const renderProductName = (
  isLoading: boolean,
  name: string,
): JSX.Element => {
  return isLoading ? (
    <Skeleton.whiteSkeleton width='611px' />
  ) : (
    <span className={`${base}__product-name`}>{setValue(name)}</span>
  )
}

export const renderActions = (
  id: string,
  isLoading: boolean,
  isCompleted: boolean,
  startWarranty: string | undefined,
  endWarranty: string | undefined,
  warrantyType: WARRANTY_TYPE | undefined,
  onOpenHistoryModal: (id: string) => void,
): JSX.Element => {
  const completedProductStatus = isCompleted && (
    <div className={`${base}__row`}>
      <IconCheckCircle className={`${base}__warranty--active`} />
      <span className={`${base}__warranty ${base}__warranty--active`}>
        {getLabel('product:completed.label')}
      </span>
    </div>
  )

  const productStatus = (
    <div className={`${base}__warranty-layout`}>
      {completedProductStatus}
      <ProductWarranty
        startWarranty={startWarranty}
        endWarranty={endWarranty}
        warrantyType={warrantyType}
      />
    </div>
  )

  const historyProduct = (
    <div
      className={`${base}__actions--history-layout`}
      onClick={() => onOpenHistoryModal(id)}
    >
      <IconHistory />
      <span className={`${base}__actions--history-text`}>
        {getLabel('ticket:history')}
      </span>
    </div>
  )

  return (
    <div className={`${base}__actions`}>
      {isLoading ? <Skeleton.whiteSkeleton width='88px' /> : productStatus}
      {isLoading ? <Skeleton.whiteSkeleton width='110px' /> : historyProduct}
    </div>
  )
}

export const renderProductInfo = (
  isLoading: boolean,
  detail: ITicketProductInfo,
): JSX.Element[] => {
  const { id, code, serialNumber, type, taskIssue } = detail

  const taskTypeTh = type ? type.nameTh : '-'
  const taskTypeEn = type ? type.nameEn : '-'
  const taskIssueTh = taskIssue ? taskIssue.nameTh : '-'
  const taskIssueEn = taskIssue ? taskIssue.nameEn : '-'

  // dropdowns
  const [selectedIssueType, setSelectedIssueType] = useState('')
  const [selectedTaskType, setSelectedTaskType] = useState('')
  const [{ data: taskType }] = useFindAllTaskType()
  const [{ data: issueType }] = useFindAllTaskIssue()
  const [issueTypeUpdating, setIssueTypeUpdating] = useState<boolean>(false)
  const [typeUpdating, setTypeUpdating] = useState<boolean>(false)
  const [issueTypeError, setIssueTypeError] = useState<boolean>(false)
  const [taskTypeError, setTaskTypeError] = useState<boolean>(false)
  const { t } = useTranslation(['ticket', 'error'])

  const skeletonProps = {
    skeletonWidthTitle: '65px',
    skeletonWidthValue: '162px',
  }

  const list: TicketLabelProps[] = [
    {
      key: 'serialNumber',
      title: 'ticket:serialNumber.title',
      value: setValue(serialNumber),
      ...skeletonProps,
    },
    {
      key: 'sku',
      title: 'ticket:sku.title',
      value: setValue(code),
      ...skeletonProps,
    },
    {
      key: 'type',
      title: TASK_TYPE_TITLE,
      value: getValueByLanguage(taskTypeTh, taskTypeEn),
      ...skeletonProps,
    },
    {
      key: 'issueType',
      title: ISSUE_TYPE_TITLE,
      value: getValueByLanguage(taskIssueTh, taskIssueEn),
      ...skeletonProps,
    },
  ]

  const handleDropdownChange = async (payload: IUpdateTaskType) => {
    if (Object.keys(payload).length == 0) {
      return
    }
    try {
      await updateTask(id, payload)
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

  const handleTaskTypeChange = async (value: string) => {
    setSelectedTaskType(value)
    try {
      await handleDropdownChange({
        type: { id: parseInt(value) },
      })
    } catch (error) {
      setTaskTypeError(true)
    }
    setTypeUpdating(false)
  }

  const handleIssueTypeChange = async (value: string) => {
    setSelectedIssueType(value)
    try {
      await handleDropdownChange({
        taskIssue: { id: parseInt(value) },
      })
    } catch (error) {
      setIssueTypeError(true)
    }
    setIssueTypeUpdating(false)
  }

  return list.map((item, index) => {
    const isIssueType = item.title === ISSUE_TYPE_TITLE
    const isTaskType = item.title === TASK_TYPE_TITLE
    return (
      <div
        key={`product-info-${index}`}
        className={`${base}__product-info--item`}
      >
        {isTaskType ? (
          <div className='ticket-description-dropdown'>
            <label>{t('ticket:type.title')}</label>
            <Dropdown
              disabled={false}
              placeholder={
                typeof item.value === 'string' && item.value !== ''
                  ? item.value
                  : ''
              }
              options={filterOptionBuilder(taskType)}
              onChange={(value) => {
                handleTaskTypeChange(value.key)
              }}
              onBlur={async () => {
                setTypeUpdating(true)
              }}
              selected={findSelected(
                filterOptionBuilder(taskType),
                selectedTaskType,
              )}
              withIcon={typeUpdating && <Loader />}
            />
            {taskTypeError && (
              <span
                className='input-group__message-error'
                data-testid='input-group-error'
              >
                {t('error:badGateway.content')}
              </span>
            )}
          </div>
        ) : isIssueType ? (
          <div className='ticket-description-dropdown'>
            <label>{t('ticket:issueType.title')}</label>
            <Dropdown
              disabled={false}
              placeholder={
                typeof item.value === 'string' && item.value !== ''
                  ? item.value
                  : ''
              }
              options={filterOptionBuilder(issueType)}
              onChange={(value) => {
                handleIssueTypeChange(value.key)
              }}
              onBlur={async () => {
                setIssueTypeUpdating(true)
              }}
              selected={findSelected(
                filterOptionBuilder(issueType),
                selectedIssueType,
              )}
              withIcon={issueTypeUpdating && <Loader />}
            />
            {issueTypeError && (
              <span
                className='input-group__message-error'
                data-testid='input-group-error'
              >
                {t('error:badGateway.content')}
              </span>
            )}
          </div>
        ) : (
          <TicketLabel
            isLoading={isLoading}
            title={item.title}
            value={item.value}
            skeletonWidthTitle={item.skeletonWidthTitle}
            skeletonWidthValue={item.skeletonWidthValue}
            isShowSkeletonTitle
          />
        )}
      </div>
    )
  })
}
